import type { BackupSelection } from "@app/core";
import { Button, Dialog } from "@app/ui";
import { useEffect, useState } from "react";

import type { I18nKey } from "../../lib/language.js";
import { useI18n } from "../../lib/useI18n.js";
import { useBackupImportStore } from "../../stores/index.js";

type Key = keyof BackupSelection;

const ROWS: { key: Key; labelKey: I18nKey }[] = [
  { key: "tasks", labelKey: "common.tasks" },
  { key: "events", labelKey: "common.events" },
  { key: "labels", labelKey: "common.labels" },
  { key: "topics", labelKey: "common.topics" },
  { key: "calendars", labelKey: "common.calendars" },
  { key: "settings", labelKey: "backup.settingsPreferences" },
];

const ALL_OFF: BackupSelection = {
  tasks: false,
  events: false,
  labels: false,
  topics: false,
  calendars: false,
  settings: false,
};

/** Choose which categories of a backup file to import, then Replace or Merge. */
export function BackupImportDialog() {
  const { t } = useI18n();
  const { open, summary, resolve } = useBackupImportStore();
  const [sel, setSel] = useState<BackupSelection>(ALL_OFF);

  useEffect(() => {
    if (!summary) return;
    // Default: everything that the file actually contains is checked.
    setSel({
      tasks: summary.tasks > 0,
      events: summary.events > 0,
      labels: summary.labels > 0,
      topics: summary.topics > 0,
      calendars: summary.calendars > 0,
      settings: summary.hasSettings,
    });
  }, [summary]);

  if (!summary) return null;

  const count: Record<Key, number | null> = {
    tasks: summary.tasks,
    events: summary.events,
    labels: summary.labels,
    topics: summary.topics,
    calendars: summary.calendars,
    settings: summary.hasSettings ? null : 0,
  };
  const available = (key: Key) => (key === "settings" ? summary.hasSettings : count[key]! > 0);
  const anyChecked = ROWS.some((row) => sel[row.key]);

  const toggle = (key: Key) => setSel((prev) => ({ ...prev, [key]: !prev[key] }));
  const done = (mode: "replace" | "merge") => resolve({ kind: "import", mode, selection: sel });

  return (
    <Dialog open={open} onClose={() => resolve({ kind: "cancel" })} title={t("backup.importTitle")} className="max-w-md">
      <div className="flex flex-col gap-4">
        <p className="text-sm text-muted-foreground">{t("backup.importDescription")}</p>

        <div className="flex flex-col gap-1">
          {ROWS.map((row) => {
            const enabled = available(row.key);
            const rowCount = count[row.key];
            return (
              <label
                key={row.key}
                className={`flex items-center gap-2 rounded-md px-1 py-1 text-sm ${enabled ? "" : "opacity-40"}`}
              >
                <input type="checkbox" checked={sel[row.key]} disabled={!enabled} onChange={() => toggle(row.key)} />
                <span className="flex-1">{t(row.labelKey)}</span>
                {row.key !== "settings" && <span className="text-xs text-muted-foreground">{rowCount}</span>}
                {row.key === "settings" && !summary.hasSettings && <span className="text-xs text-muted-foreground">{t("backup.none")}</span>}
              </label>
            );
          })}
        </div>

        <div className="flex flex-col gap-2">
          <Button variant="primary" disabled={!anyChecked} onClick={() => done("merge")}>
            {t("backup.merge")}
          </Button>
          <Button variant="destructive" disabled={!anyChecked} onClick={() => done("replace")}>
            {t("backup.replace")}
          </Button>
          <Button variant="ghost" onClick={() => resolve({ kind: "cancel" })}>{t("common.cancel")}</Button>
        </div>
      </div>
    </Dialog>
  );
}
